import type WebApiClient from "../../api/WebApiClient.js";
import { buildCopyGroupsMessage, buildSavedGroupMessage } from "../../app-utils/alert.js";
import { SelMessage } from "../../messages/SelMessage.js";
import { notify } from "../../modules/notifications/notify.js";
import { type IAppState } from "../reducer.js";
import { type ReduxAction, createReducer } from "../redux-utils.js";
import { loadUserParams } from "./auth.js";
import { refreshTrackCommunityAnnotations } from "./genome-browser.js";
import { type ITableDataState } from "./table-data.js";
import { type ICreateGroupParams } from "@/api/WebApiClient.ts";
import { createErrorHandler } from "@/app-utils/errors.ts";
import { pushRoute } from "@/app-utils/route.ts";
import {
  type Group,
  type InputCreateGroupInput,
  type InputModifySavedItemInput,
  type InputTableQuerySpec,
  type Project,
  type SavedItem,
  type SavedItemsListing,
  type SequenceGroupTypeCount,
  type UserGroupItemValidationResult,
  type View,
} from "@/graphql.ts";
import { selMessage } from "@/messages/message.ts";
import {
  COPY_GROUPS_FAILURE,
  COPY_GROUPS_REQUEST,
  COPY_GROUPS_SUCCESS,
  CREATE_GROUP_FAILURE,
  CREATE_GROUP_REQUEST,
  CREATE_GROUP_SUCCESS,
  CREATE_TEMP_GROUP_FAILURE,
  DELETE_GROUPS_FAILURE,
  DELETE_SAVED_ITEM_SUCCESS,
  LOAD_GROUP_FAILURE,
  LOAD_GROUP_REQUEST,
  LOAD_GROUP_SUCCESS,
  LOAD_VIEW_SUCCESS,
  SET_VISIBLE_MUTANT_GENOMES,
  UPDATE_SAVED_ITEM_FAILURE,
  UPDATE_SAVED_ITEM_REQUEST,
  UPDATE_SAVED_ITEM_SUCCESS,
  VIEW_UNION_GROUPS_FAILURE,
} from "@/redux/action-types.ts";
import { VIEW } from "@/settings/settings.ts";

/**
 * State definition
 */
export interface ISavedGroupState {
  isLoading: boolean;
  group: Group;
  visibleMutantGenomeIds: number[];
}

const initialState: ISavedGroupState = {
  // fetching
  group: null,
  isLoading: false,
  visibleMutantGenomeIds: [],
};

/**
 * Reducers
 */
export default createReducer(initialState, {
  [LOAD_VIEW_SUCCESS]: (state: ITableDataState, view: View) => {
    if (view.viewType === VIEW.TABLE_VIEW.type) {
      return {
        ...state,
        visibleMutantGenomeIds: view.state.tableView.tableQuerySpec?.restrictions?.variantGenomeIds || [],
      };
    }
    return state;
  },

  [LOAD_GROUP_REQUEST]: (state: ISavedGroupState) => {
    return { ...state, isLoading: true, group: null };
  },
  [LOAD_GROUP_SUCCESS]: (state: ISavedGroupState, payload: Group) => {
    return { ...state, isLoading: false, group: payload };
  },
  [LOAD_GROUP_FAILURE]: (state: ISavedGroupState) => {
    return { ...state, isLoading: false };
  },
  // CREATE_GROUP_{REQUEST,SUCCESS,FAILURE} intentionally does not replace the group attribute of ISavedGroupState
  //    Otherwise a "Save As" action from the table display of a Group would replace the current group.
  //    That leaves nothing to do for CREATE_GROUP action.
  [SET_VISIBLE_MUTANT_GENOMES]: (state: ISavedGroupState, payload: number[]) => {
    return { ...state, visibleMutantGenomeIds: payload };
  },
});

/**
 * Action creators
 */

function initiateCreateGroupRequest() {
  return {
    type: CREATE_GROUP_REQUEST,
    //payload: payload,
  };
}

function createGroupRequestSuccess(response: Group) {
  return {
    type: CREATE_GROUP_SUCCESS,
    payload: response,
  };
}

export function saveSelectedRows(payload: ICreateGroupParams) {
  return (dispatch, getState: () => IAppState, webApiClient: WebApiClient): Promise<Group> => {
    const state: IAppState = getState();
    const refGenomeId: number = state.auth.session?.params.referenceGenome;

    const { project: _, ...others } = payload;
    const createGroupInput: InputCreateGroupInput = {
      ...others,
      projectId: payload.project.id,
      temporary: false,
      tableQuerySpec: {
        ...payload.tableQuerySpec,
        restrictions: {
          ...payload.tableQuerySpec.restrictions,
          refGenomeId: refGenomeId,
        },
      },
    };

    dispatch(initiateCreateGroupRequest());
    return webApiClient
      .saveSelectionAsGroup(createGroupInput)
      .then((response: Group) => {
        const group = response;
        const message = buildSavedGroupMessage(group, payload.project);

        dispatch(createGroupRequestSuccess(response));

        // Reload current lastSaveTarget from configuration
        dispatch(loadUserParams());

        // refresh potential group indicators in Genome Browser
        dispatch(refreshTrackCommunityAnnotations(payload.tableQuerySpec.resultType, group.itemSubTypeId));

        notify.info(
          {
            title: selMessage({ id: "group.save.successTitle" }),
            children: message,
          },
          { autoClose: 10000 },
        );
        return response;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: CREATE_GROUP_FAILURE,
          alertTitle: "Group Creation Request Failed",
          alertMessage: `The group "${payload.name}" could not be created in our database`,
          showAlert: false,
          throwError: true,
        }),
      );
  };
}

function fetchGroupRequest() {
  return {
    type: LOAD_GROUP_REQUEST,
  };
}

function fetchGroupRequestSuccess(response: Group) {
  return {
    type: LOAD_GROUP_SUCCESS,
    payload: response,
  };
}

export function shouldLoadGroup(groupId: number) {
  return (dispatch, getState, webApiClient: WebApiClient): Promise<Group> => {
    const savedGroupState: ISavedGroupState = getState().savedGroup;

    if (!savedGroupState.isLoading && (!savedGroupState.group || savedGroupState.group.id !== groupId)) {
      dispatch(fetchGroupRequest());

      return webApiClient
        .fetchGroup(groupId)
        .then((response: Group) => {
          dispatch(fetchGroupRequestSuccess(response));
          return response;
        })
        .catch(
          createErrorHandler(dispatch, {
            actionType: LOAD_GROUP_FAILURE,
            alertTitle: "Fetching of Group Failed",
            alertMessage: `The saved group ${groupId} could not be searched against our database`,
          }),
        );
    } else {
      return Promise.resolve(savedGroupState.group);
    }
  };
}

export function deleteGroups(groupIds: number[], groupType: string) {
  return (dispatch, _getState, webApiClient: WebApiClient): Promise<Group[]> => {
    return webApiClient
      .deleteGroupList(groupIds)
      .then((response: Group[]) => {
        // dispatch(push("/home"));
        dispatch({
          type: DELETE_SAVED_ITEM_SUCCESS,
          payload: {
            deletedIds: groupIds,
            type: groupType,
          },
        });

        notify.info({
          title: selMessage({
            id: "group.delete.successTitle",
            values: { groupCount: groupIds.length },
          }),
          children: SelMessage({
            id: "group.delete.successMessage",
            values: { groupCount: groupIds.length },
          }),
        });
        return response;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: DELETE_GROUPS_FAILURE,
          alertTitle: selMessage({
            id: "group.delete.errorTitle",
            values: { groupCount: groupIds.length },
          }),
          alertMessage: selMessage({
            id: "group.delete.errorMessage",
            values: { groupsCount: groupIds.length },
          }),
        }),
      );
  };
}

function initiateCopyGroupsRequest(groupIds: number[], groupType: string, project: Project, isPrivate: boolean) {
  return {
    type: COPY_GROUPS_REQUEST,
    payload: {
      groupIds,
      groupType,
      project,
      isPrivate,
    },
  };
}

function copyGroupsRequestSuccess(response: boolean) {
  return {
    type: COPY_GROUPS_SUCCESS,
    payload: response,
  };
}

export function copyGroups(groupIds: number[], project: Project, isPrivate: boolean, meta: SavedItemsListing) {
  return (dispatch, _getState, webApiClient: WebApiClient): Promise<boolean> => {
    dispatch(initiateCopyGroupsRequest(groupIds, meta.subType, project.id, isPrivate));
    return webApiClient
      .copyGroups(groupIds, project.id, isPrivate)
      .then((response: boolean) => {
        dispatch(copyGroupsRequestSuccess(response));

        // Reload current lastSaveTarget from configuration
        dispatch(loadUserParams());

        notify.info(
          {
            title: selMessage({ id: "group.copy.successTitle", values: { groupCount: groupIds.length } }),
            children: buildCopyGroupsMessage(groupIds.length, meta.subType, project),
          },
          { autoClose: 10000 },
        );

        return true;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: COPY_GROUPS_FAILURE,
          alertTitle: "Copy Groups Request Failed",
          alertMessage: `The groups could not be copied in our database`,
        }),
      );
  };
}

export function validateSingleGroupAgainstProjectUserGroup(
  projectId: number,
  type: string,
  selectionSpec: InputTableQuerySpec,
  targetType: string,
) {
  return (
    _dispatch,
    _getState: () => IAppState,
    webApiClient: WebApiClient,
  ): Promise<UserGroupItemValidationResult> => {
    return webApiClient.validateSingleGroupAgainstProjectUserGroup(projectId, type, selectionSpec, targetType);
  };
}

export function validateSavedGroupsAgainstProjectUserGroup(projectId: number, groupIds: number[]) {
  return (_dispatch, _getState, webApiClient: WebApiClient): Promise<boolean> => {
    return webApiClient.validateSavedGroupsAgainstProjectUserGroup(projectId, groupIds);
  };
}

export function viewUnion(groupIds: number[]) {
  return (dispatch, _getState, webApiClient: WebApiClient): Promise<boolean> => {
    return webApiClient.createGroupsUnion(groupIds).then(
      (unionGroupId: number) => {
        return pushRoute(`/group/${unionGroupId}`);
      },
      createErrorHandler(dispatch, {
        actionType: VIEW_UNION_GROUPS_FAILURE,
        alertTitle: "View Groups Union Request Failed",
        alertMessage: `The union of the selected groups could not be executed.`,
      }),
    );
  };
}

export function viewUnionOfSamplesFromParents(parentIds: number[]) {
  return (dispatch, _getState, webApiClient: WebApiClient): Promise<boolean> => {
    return webApiClient.createSampleGroupFromParentsUnion(parentIds).then(
      (unionGroupId: number) => {
        return pushRoute(`/group/${unionGroupId}`);
      },
      createErrorHandler(dispatch, {
        actionType: VIEW_UNION_GROUPS_FAILURE,
        alertTitle: "View Union of Samples From Parents Request Failed",
        alertMessage: `The union of samples of the selected sample parents could not be executed.`,
      }),
    );
  };
}

export function createTempGroup(selectionSpec: InputTableQuerySpec) {
  return (dispatch, _getState, webApiClient: WebApiClient): Promise<number> => {
    return webApiClient.createTempGroup(selectionSpec).then(
      (groupId: number) => groupId,
      createErrorHandler(
        dispatch, // TODO
        {
          actionType: CREATE_TEMP_GROUP_FAILURE,
          alertTitle: "Group Creation Failure",
          alertMessage: `Could not create a group out of your selection.`,
        },
      ),
    );
  };
}

export function setGroupMutantGenomeIds(visibleMutantGenomeIds: number[]) {
  return {
    type: SET_VISIBLE_MUTANT_GENOMES,
    payload: visibleMutantGenomeIds,
  };
}

export function getSequenceGroupTypes(tableQuerySpec: InputTableQuerySpec) {
  return (dispatch, _getState, webApiClient: WebApiClient): Promise<SequenceGroupTypeCount[]> => {
    return webApiClient.getSequenceGroupTypes(tableQuerySpec).catch(
      createErrorHandler(dispatch, {
        actionType: CREATE_TEMP_GROUP_FAILURE,
        alertTitle: "Getting Sequence Group Types Failure",
        alertMessage: `Could not infer from the selection the various types available for group creation.`,
      }),
    );
  };
}

export function updateSavedItem(data: InputModifySavedItemInput): ReduxAction<Promise<SavedItem>> {
  return (dispatch, _getState, webApiClient) => {
    dispatch({ type: UPDATE_SAVED_ITEM_REQUEST });

    return webApiClient
      .updateSavedItem(data)
      .then((updatedSavedItem: SavedItem) => {
        dispatch({
          type: UPDATE_SAVED_ITEM_SUCCESS,
          payload: updatedSavedItem,
        });
        return updatedSavedItem;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: UPDATE_SAVED_ITEM_FAILURE,
          showAlert: false,
          throwError: true,
        }),
      );
  };
}
